import { type FC, useMemo, useState } from "react";

import type { DateRangePartial } from "@alpacahq/ui/old";

import {
  useGetFPSLAnalytics,
  useGetFPSLLoans,
} from "src/v2/api/hooks/useGetFPSL";
import { useAccountContext } from "src/v2/providers/AccountProvider";
import { StockLendingInterestTable } from "./components/StockLendingInterestTable";
import { ProgramInterestPres } from "./ProgramInterest.pres";

const ProgramInterest: FC = () => {
  const { currentAccount, product } = useAccountContext();
  const [searchSymbol, setSearchSymbol] = useState("");
  const [dateRange, setDateRange] = useState<DateRangePartial | undefined>();

  const { analytics, isLoading: analyticsLoading } = useGetFPSLAnalytics({
    accountID: currentAccount?.id,
    product,
    options: {
      enabled: !!currentAccount?.id && product === "live",
    },
  });

  // Convert DateRangePartial to [Date | null, Date | null] for the hook
  const convertedDateRange = useMemo<[Date | null, Date | null]>(() => {
    return [dateRange?.[0] || null, dateRange?.[1] || null];
  }, [dateRange]);

  // Fetch filtered loans for display in the table
  const { loans, isLoading: loansLoading } = useGetFPSLLoans({
    accountID: currentAccount?.id,
    product,
    params: {
      symbol: searchSymbol,
      dateRange: convertedDateRange,
    },
    options: {
      enabled: !!currentAccount?.id && product === "live",
    },
  });

  // Fetch unfiltered loans to calculate the "Last Updated" date
  const { loans: unfilteredLoans } = useGetFPSLLoans({
    accountID: currentAccount?.id,
    product,
    options: {
      enabled: !!currentAccount?.id && product === "live",
    },
  });

  // Calculate the most recent loan date from unfiltered data
  const latestLoanDate = useMemo(() => {
    if (!unfilteredLoans || unfilteredLoans.length === 0) {
      return undefined;
    }

    const mostRecent = unfilteredLoans.reduce((latest, loan) => {
      if (!latest) {
        return loan;
      }

      return new Date(loan.date) > new Date(latest.date) ? loan : latest;
    }, unfilteredLoans[0]);

    return mostRecent?.date;
  }, [unfilteredLoans]);

  // Use default values while loading or if data is missing
  const estimatedInterest = analytics?.interest?.customer ?? 0;
  const stocksLent = analytics?.total_lending_activities ?? 0;
  const lendingsInProgress = analytics?.in_progress_lending_activities ?? 0;

  const handleSearchChange = (symbol: string) => {
    setSearchSymbol(symbol);
  };

  const handleDateRangeChange = (range: DateRangePartial) => {
    // Handle cleared date range
    if (range && range[0] === null && range[1] === null) {
      setDateRange(undefined);
    } else {
      setDateRange(range);
    }
  };

  return (
    <div className="mx-4 min-h-screen">
      <ProgramInterestPres
        estimatedInterest={estimatedInterest}
        stocksLent={stocksLent}
        lendingsInProgress={lendingsInProgress}
        latestLoanDate={latestLoanDate}
        loading={analyticsLoading}
      />
      <StockLendingInterestTable
        loans={loans || []}
        loading={loansLoading}
        onSearchChange={handleSearchChange}
        onDateRangeChange={handleDateRangeChange}
      />
    </div>
  );
};

export default ProgramInterest;
